import { Box, Switch, Text } from '@chakra-ui/react';

interface CookModeToggleProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** `hero`: glass pill over the mobile recipe hero. `band`: bordered row in the desktop stats band. */
  variant: 'hero' | 'band';
}

/** "Cook mode" switch — keeps the screen awake while on. */
export default function CookModeToggle({ checked, onCheckedChange, variant }: CookModeToggleProps) {
  const isHero = variant === 'hero';

  return (
    <Switch.Root
      size={isHero ? 'md' : 'sm'}
      checked={checked}
      onCheckedChange={(e) => onCheckedChange(e.checked)}
      display="inline-flex" alignItems="center"
      {...(isHero
        ? {
            gap: '10px', pl: 4, pr: '7px', py: '7px',
            borderRadius: 'full',
            border: '1px solid', borderColor: 'rgba(255,255,255,0.35)',
            style: { backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', background: 'rgba(0,0,0,0.32)' },
          }
        : {
            gap: '10px', pl: '14px', pr: 2, py: '6px',
            borderRadius: 'lg',
            border: '1px solid',
            borderColor: checked ? 'accent.secondaryBorder' : 'border.muted',
            bg: checked ? 'accent.secondarySubtle' : 'bg.canvas',
            transition: 'background 0.15s, border-color 0.15s',
          })}
    >
      {/* Chakra renders a plain checkbox; role="switch" makes it announce as a switch */}
      <Switch.HiddenInput role="switch" />
      {/* Flex column so the inline label doesn't sit on an inherited line-height
          baseline, which pushes the text below the switch's centre */}
      <Box display="flex" flexDirection="column" gap="2px">
        <Switch.Label
          fontSize={isHero ? '14px' : '13px'} fontWeight="600"
          color={isHero ? 'white' : 'text.primary'}
        >
          Cook mode
        </Switch.Label>
        {!isHero && (
          <Text fontSize="11px" color="text.muted" lineHeight="1.2">Keeps your screen on</Text>
        )}
      </Box>
      <Switch.Control
        bg={isHero ? 'rgba(255,255,255,0.35)' : 'border.muted'}
        _checked={{ bg: 'accent.secondary' }}
      >
        <Switch.Thumb />
      </Switch.Control>
    </Switch.Root>
  );
}
